import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Button,
  Paper,
  alpha,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useGetSalesOrderQuery } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { shareSalesOrderPdf } from '../utils/exportSalesOrderPdf';
import { SalesInvoiceTemplateRenderer } from '../components/SalesInvoiceTemplateRenderer';

function SalesOrderPrint() {
  const { id } = useParams();
  const { data: order, isLoading, error } = useGetSalesOrderQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();

  const currency = company?.currency || 'USD';
  const itemFormat = company?.printItemNameFormat || 'name_sku';
  const salesTemplate = company?.salesPrintTemplate || 'standard';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';
  const templateLabelMap = {
    standard: 'Template 1 – Standard A4',
    thermal: 'Template 2 – Thermal',
    template3: 'Template 3',
    template4: 'Template 4',
    template5: 'Template 5',
    template6: 'Template 6',
    template7: 'Template 7',
    template8: 'Template 8',
    template9: 'Template 9',
    template10: 'Template 10',
    template11: 'Template 11 – Tax Invoice Grid',
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const formatItemName = (item) => {
    return formatItemDisplayName(
      { name: item.product?.name, sku: item.product?.sku, brand: item.product?.brand },
      itemFormat
    );
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  useEffect(() => {
    if (order) {
      // small delay so layout renders before print dialog
      const t = setTimeout(() => {
        window.print();
      }, 300);
      return () => clearTimeout(t);
    }
  }, [order]);

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error || !order) return <Typography color="error">Error loading sales order</Typography>;

  const handleShare = () => {
    shareSalesOrderPdf(order, company, {
      itemFormat: company?.printItemNameFormat || 'name_sku',
      filename: (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    });
  };

  const invoiceDate = formatDate(order.invoiceDate);
  const items = order.items || [];

  // ── Standard A4 layout (default) ─────────────────────────────
  const renderStandard = () => (
    <Box
      sx={{
        p: 4,
        maxWidth: 900,
        m: '0 auto',
        fontSize: 12,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box
              component="img"
              src={company.logoUrl}
              alt=""
              sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain', display: 'block' }}
            />
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2">Invoice No: {order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order.salesType}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1, mr: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order.customer?.name}</Typography>
          {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 2 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>VAT</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Line Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(order.items || []).map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  {formatItemName(it)}
                </Typography>
                {it.batchNo && <Typography sx={{ fontSize: 11 }}>Batch: {it.batchNo}</Typography>}
                {it.expiryDate && (
                  <Typography sx={{ fontSize: 11 }}>
                    Exp: {new Date(it.expiryDate).toLocaleDateString()}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell>{formatAmount(it.unitPrice)}</TableCell>
              <TableCell>{formatAmount(it.discount || 0)}</TableCell>
              <TableCell>{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order.grandTotal || order.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order.notes && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ── Thermal / narrow receipt layout ─────────────────────────
  const renderThermal = () => (
    <Box
      sx={{
        p: 2,
        maxWidth: 420,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 1,
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        {company?.logoUrl && (
          <Box
            component="img"
            src={company.logoUrl}
            alt=""
            sx={{ height: 36, width: 'auto', maxWidth: 100, objectFit: 'contain', mx: 'auto', mb: 0.5, display: 'block' }}
          />
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
        <Typography variant="caption" display="block">
          Invoice: {order.invoiceNumber || order.orderNumber}
        </Typography>
        <Typography variant="caption" display="block">
          Date: {invoiceDate}
        </Typography>
        <Typography variant="caption" display="block">
          Customer: {order.customer?.name || '-'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Table size="small" aria-label="items" sx={{ '& td, & th': { borderBottom: 'none', paddingY: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: 11 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell sx={{ fontSize: 11 }}>
                {formatItemName(it)}
                {it.batchNo && (
                  <Typography variant="caption" display="block">
                    Batch: {it.batchNo}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {Number(it.quantity || 0).toFixed(2)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.unitPrice)}
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 11 }}>
                {formatAmount(it.total || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Subtotal</Typography>
          <Typography variant="caption">{formatAmount(order.subtotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Discount</Typography>
          <Typography variant="caption">{formatAmount(order.discountTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">VAT</Typography>
          <Typography variant="caption">{formatAmount(order.vatTotal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Grand Total
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {formatAmount(order.grandTotal || order.totalAmount)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Paid</Typography>
          <Typography variant="caption">{formatAmount(order.paidAmount)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Balance</Typography>
          <Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography>
        </Box>
      </Box>

      {order.notes && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption">Thank you for your business!</Typography>
      </Box>
    </Box>
  );

  // ── Template 3 – Minimal A4 layout ──────────────────────────
  const renderTemplate3 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 820,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        {company?.logoUrl && (
          <Box component="img" src={company.logoUrl} alt="" sx={{ height: 40, width: 'auto', maxWidth: 100, objectFit: 'contain', mx: 'auto', mb: 0.5, display: 'block' }} />
        )}
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {company?.name || 'Company'}
        </Typography>
        {company?.address && (
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {company.address}
          </Typography>
        )}
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Bill To
          </Typography>
          <Typography variant="caption" display="block">
            {order.customer?.name}
          </Typography>
          {order.customer?.address && (
            <Typography variant="caption" display="block">
              {order.customer.address}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="caption" display="block">
            Invoice: {order.invoiceNumber || order.orderNumber}
          </Typography>
          <Typography variant="caption" display="block">
            Date: {invoiceDate}
          </Typography>
          <Typography variant="caption" display="block">
            Sales Type: {order.salesType}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Template 3 – Minimal A4
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      <Table size="small" aria-label="items" sx={{ '& th': { borderBottomWidth: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
                  {formatItemName(it)}
                </Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 240 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatAmount(order.grandTotal || order.totalAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // ── Template 4 – Two-column summary layout ───────────────────
  const renderTemplate4 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 1000,
        m: '0 auto',
        fontSize: 11,
        '@media print': {
          boxShadow: 'none',
          m: 0,
          p: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            TAX INVOICE
          </Typography>
          <Typography variant="caption" display="block">
            Invoice: {order.invoiceNumber || order.orderNumber}
          </Typography>
          <Typography variant="caption" display="block">
            Date: {invoiceDate}
          </Typography>
          <Typography variant="caption" display="block">
            Sales Type: {order.salesType}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Template 4 – Summary
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Customer
          </Typography>
          <Typography variant="caption" display="block">
            {order.customer?.name}
          </Typography>
          {order.customer?.address && (
            <Typography variant="caption" display="block">
              {order.customer.address}
            </Typography>
          )}
          {order.customer?.trn && (
            <Typography variant="caption" display="block">
              TRN: {order.customer.trn}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {order.location && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="caption" display="block">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table size="small" sx={{ mt: 1 }} aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Disc.
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>
                <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
                  {formatItemName(it)}
                </Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ flex: 1 }}>
          {order.notes && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>
                {order.notes}
              </Typography>
            </>
          )}
        </Box>
        <Box sx={{ width: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Subtotal</Typography>
            <Typography variant="caption">{formatAmount(order.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">{formatAmount(order.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">VAT</Typography>
            <Typography variant="caption">{formatAmount(order.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatAmount(order.grandTotal || order.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Paid</Typography>
            <Typography variant="caption">{formatAmount(order.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Balance</Typography>
            <Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // ── Template 5 – Boxed A4 layout ─────────────────────────────
  const renderTemplate5 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 900,
        m: '16px auto',
        fontSize: 12,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#FFFFFF',
        '@media print': {
          m: 0,
          borderRadius: 0,
          borderColor: '#CCCCCC',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {company?.name || 'Company'}
            </Typography>
            {company?.address && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {company.address}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Invoice
          </Typography>
          <Typography variant="body2">No: {order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order.salesType}</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Bill To
          </Typography>
          <Typography variant="body2">{order.customer?.name}</Typography>
          {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {order.location && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Branch / Store
              </Typography>
              <Typography variant="body2">
                {order.location.name} ({order.location.code})
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Table
        size="small"
        aria-label="items"
        sx={{
          mt: 1,
          '& th, & td': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Unit
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Discount
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              VAT
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Line Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  {formatItemName(it)}
                </Typography>
              </TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box
          sx={{
            width: 260,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatAmount(order.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Discount</Typography>
            <Typography variant="body2">{formatAmount(order.discountTotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">VAT</Typography>
            <Typography variant="body2">{formatAmount(order.vatTotal)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Grand Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatAmount(order.grandTotal || order.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Paid</Typography>
            <Typography variant="body2">{formatAmount(order.paidAmount)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Balance</Typography>
            <Typography variant="body2">{formatAmount(order.balanceAmount)}</Typography>
          </Box>
        </Box>
      </Box>

      {order.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {order.notes}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ── Template 6 – Detailed invoice (full columns, section borders) ─────
  const renderTemplate6 = () => (
    <Box
      sx={{
        p: 3,
        maxWidth: 920,
        m: '0 auto',
        fontSize: 12,
        border: '2px solid',
        borderColor: 'grey.300',
        borderRadius: 1,
        '@media print': { boxShadow: 'none', m: 0, borderColor: '#999' },
      }}
    >
      <Box sx={{ borderBottom: '2px solid', borderColor: 'grey.300', pb: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 48, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {company?.name || 'Company'}
              </Typography>
              {company?.address && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                  {company.address}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>DETAILED INVOICE</Typography>
            <Typography variant="body2">No: {order.invoiceNumber || order.orderNumber}</Typography>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
            <Typography variant="body2">Sales Type: {order.salesType}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Bill To</Typography>
          <Typography variant="body2">{order.customer?.name}</Typography>
          {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
          {order.customer?.trn && <Typography variant="body2">TRN: {order.customer.trn}</Typography>}
        </Box>
        {order.location && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Branch / Store</Typography>
            <Typography variant="body2">{order.location.name} ({order.location.code})</Typography>
          </Box>
        )}
      </Box>
      <Table size="small" sx={{ '& th, & td': { border: '1px solid', borderColor: 'divider' } }} aria-label="items">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.200' }}>
            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Discount</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">VAT</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Line Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography>
                {it.batchNo && <Typography variant="caption">Batch: {it.batchNo}</Typography>}
                {it.expiryDate && <Typography variant="caption" display="block">Exp: {new Date(it.expiryDate).toLocaleDateString()}</Typography>}
              </TableCell>
              <TableCell>{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell>{it.unit || 'pcs'}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Subtotal</Typography><Typography variant="body2">{formatAmount(order.subtotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Discount</Typography><Typography variant="body2">{formatAmount(order.discountTotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">VAT</Typography><Typography variant="body2">{formatAmount(order.vatTotal)}</Typography></Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><Typography variant="body2">Paid</Typography><Typography variant="body2">{formatAmount(order.paidAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Balance</Typography><Typography variant="body2">{formatAmount(order.balanceAmount)}</Typography></Box>
        </Box>
      </Box>
      {order.notes && (<Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}><Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography></Box>)}
    </Box>
  );

  // ── Template 7 – Retail style (bold header, compact, emphasis on total) ─────
  const renderTemplate7 = () => (
    <Box sx={{ p: 2, maxWidth: 800, m: '0 auto', fontSize: 11, '@media print': { m: 0, p: 1 } }}>
      <Box sx={{ textAlign: 'center', borderBottom: '3px solid', borderColor: 'primary.main', pb: 1.5, mb: 1.5 }}>
        {company?.logoUrl && (
          <Box component="img" src={company.logoUrl} alt="" sx={{ height: 36, width: 'auto', maxWidth: 100, objectFit: 'contain', mx: 'auto', mb: 0.5, display: 'block' }} />
        )}
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{company?.name || 'Company'}</Typography>
        {company?.address && <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{company.address}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Customer:</Typography>
          <Typography variant="body2">{order.customer?.name}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2">Invoice #{order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
        </Box>
      </Box>
      <Table size="small" aria-label="items" sx={{ '& td, & th': { py: 0.4 } }}>
        <TableHead>
          <TableRow sx={{ borderBottom: '2px solid', borderColor: 'grey.400' }}>
            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell><Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography></TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 320, ml: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Subtotal</Typography><Typography variant="body2">{formatAmount(order.subtotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Discount</Typography><Typography variant="body2">{formatAmount(order.discountTotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">VAT</Typography><Typography variant="body2">{formatAmount(order.vatTotal)}</Typography></Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>TOTAL</Typography><Typography variant="body2" sx={{ fontWeight: 800, fontSize: 13 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption">{formatAmount(order.paidAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Balance</Typography><Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography></Box>
      </Box>
      {order.notes && <Box sx={{ mt: 1.5 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Notes:</Typography><Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography></Box>}
    </Box>
  );

  // ── Template 8 – Service invoice (clean, professional, prominent totals) ─────
  const renderTemplate8 = () => (
    <Box sx={{ p: 3, maxWidth: 700, m: '0 auto', fontSize: 12, '@media print': { m: 0, p: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{company?.name || 'Company'}</Typography>
            {company?.address && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{company.address}</Typography>}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>SERVICE INVOICE</Typography>
          <Typography variant="body2" display="block">No: {order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2" display="block">Date: {invoiceDate}</Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Bill To</Typography>
        <Typography variant="body2">{order.customer?.name}</Typography>
        {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Box>
      <Table size="small" aria-label="items">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell><Typography sx={{ fontWeight: 500 }}>{formatItemName(it)}</Typography></TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Subtotal</Typography><Typography variant="body2">{formatAmount(order.subtotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Discount</Typography><Typography variant="body2">{formatAmount(order.discountTotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">VAT</Typography><Typography variant="body2">{formatAmount(order.vatTotal)}</Typography></Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Amount Due</Typography><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}><Typography variant="caption">Paid</Typography><Typography variant="caption">{formatAmount(order.paidAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Balance</Typography><Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography></Box>
      </Box>
      {order.notes && <Box sx={{ mt: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography></Box>}
    </Box>
  );

  // ── Template 9 – Landscape-style (wide layout, two-column header) ─────
  const renderTemplate9 = () => (
    <Box sx={{ p: 2, maxWidth: 1100, m: '0 auto', fontSize: 11, '@media print': { m: 0 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {company?.logoUrl && (
            <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{company?.name || 'Company'}</Typography>
            {company?.address && <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{company.address}</Typography>}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Bill To</Typography>
            <Typography variant="body2" display="block">{order.customer?.name}</Typography>
            {order.customer?.address && <Typography variant="caption" display="block">{order.customer.address}</Typography>}
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>INVOICE</Typography>
          <Typography variant="body2">No: {order.invoiceNumber || order.orderNumber}</Typography>
          <Typography variant="body2">Date: {invoiceDate}</Typography>
          <Typography variant="body2">Sales Type: {order.salesType}</Typography>
          {order.location && <Typography variant="caption" display="block" sx={{ mt: 1 }}>{order.location.name} ({order.location.code})</Typography>}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Table size="small" aria-label="items" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600, width: '5%' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '35%' }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '12%' }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">Unit Price</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">Disc.</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '14%' }} align="right">VAT</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '16%' }} align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell><Typography sx={{ fontSize: 11 }}>{formatItemName(it)}</Typography></TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.discount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.vatAmount || 0)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box sx={{ width: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Subtotal</Typography><Typography variant="caption">{formatAmount(order.subtotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Discount</Typography><Typography variant="caption">{formatAmount(order.discountTotal)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">VAT</Typography><Typography variant="caption">{formatAmount(order.vatTotal)}</Typography></Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption">{formatAmount(order.paidAmount)}</Typography></Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Balance</Typography><Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography></Box>
        </Box>
      </Box>
      {order.notes && <Box sx={{ mt: 2 }}><Typography variant="caption" sx={{ fontWeight: 600 }}>Notes</Typography><Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography></Box>}
    </Box>
  );

  // ── Template 10 – Custom / card-style layout ─────
  const renderTemplate10 = () => (
    <Box sx={{ p: 3, maxWidth: 880, m: '0 auto', fontSize: 12, '@media print': { m: 0 } }}>
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: alpha('#1976d2', 0.04) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {company?.logoUrl && (
              <Box component="img" src={company.logoUrl} alt="" sx={{ height: 44, width: 'auto', maxWidth: 110, objectFit: 'contain' }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>{company?.name || 'Company'}</Typography>
              {company?.address && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{company.address}</Typography>}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>Invoice</Typography>
            <Typography variant="body2">No: {order.invoiceNumber || order.orderNumber}</Typography>
            <Typography variant="body2">Date: {invoiceDate}</Typography>
          </Box>
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Customer</Typography>
        <Typography variant="body2">{order.customer?.name}</Typography>
        {order.customer?.address && <Typography variant="body2">{order.customer.address}</Typography>}
      </Paper>
      <Table size="small" component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }} aria-label="items">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Price</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={it.id || idx}>
              <TableCell>{idx + 1}</TableCell>
              <TableCell>{formatItemName(it)}</TableCell>
              <TableCell align="right">{Number(it.quantity || 0).toFixed(2)}</TableCell>
              <TableCell align="right">{formatAmount(it.unitPrice)}</TableCell>
              <TableCell align="right">{formatAmount(it.total || 0)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, maxWidth: 300, ml: 'auto', bgcolor: alpha('#1976d2', 0.04) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Subtotal</Typography><Typography variant="body2">{formatAmount(order.subtotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Discount</Typography><Typography variant="body2">{formatAmount(order.discountTotal)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">VAT</Typography><Typography variant="body2">{formatAmount(order.vatTotal)}</Typography></Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(order.grandTotal || order.totalAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><Typography variant="caption">Paid</Typography><Typography variant="caption">{formatAmount(order.paidAmount)}</Typography></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Balance</Typography><Typography variant="caption">{formatAmount(order.balanceAmount)}</Typography></Box>
      </Paper>
      {order.notes && <Paper elevation={0} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{order.notes}</Typography></Paper>}
    </Box>
  );

  // Map template key to actual layout component
  const templateKey = [
    'standard',
    'thermal',
    'template3',
    'template4',
    'template5',
    'template6',
    'template7',
    'template8',
    'template9',
    'template10',
    'template11',
  ].includes(salesTemplate)
    ? salesTemplate
    : 'standard';

  let printContent;
  if (templateKey === 'thermal') {
    printContent = renderThermal();
  } else if (templateKey === 'template3') {
    printContent = renderTemplate3();
  } else if (templateKey === 'template4') {
    printContent = renderTemplate4();
  } else if (templateKey === 'template5') {
    printContent = renderTemplate5();
  } else if (templateKey === 'template6') {
    printContent = renderTemplate6();
  } else if (templateKey === 'template7') {
    printContent = renderTemplate7();
  } else if (templateKey === 'template8') {
    printContent = renderTemplate8();
  } else if (templateKey === 'template9') {
    printContent = renderTemplate9();
  } else if (templateKey === 'template10') {
    printContent = renderTemplate10();
  } else if (templateKey === 'template11') {
    printContent = (
      <SalesInvoiceTemplateRenderer
        order={order}
        company={company}
        templateKey="template11"
        currency={currency}
        itemFormat={itemFormat}
        dateFormat={dateFormat}
      />
    );
  } else {
    printContent = renderStandard();
  }

  const paperType = company?.printPaperType || 'a4';

  return (
    <Box>
      {paperType === 'a5' && (
        <style>{`@page { size: A5; }`}</style>
      )}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          '@media print': { display: 'none' },
        }}
      >
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ borderRadius: 2 }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleShare}
          sx={{ borderRadius: 2 }}
        >
          Share
        </Button>
      </Box>
      {printContent}
    </Box>
  );
}

export default SalesOrderPrint;

